// components/DeploymentSuccess.tsx
import React from 'react';

interface DeploymentSuccessProps {
  url: string;
  subdomain: string;
  onClose: () => void;
  onCreateAnother: () => void;
}

const DeploymentSuccess: React.FC<DeploymentSuccessProps> = ({ 
  url, 
  subdomain, 
  onClose, 
  onCreateAnother 
}) => {
  const handleLaunchWebsite = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: '#F8FAFC',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '520px',
        background: '#FFFFFF',
        borderRadius: '24px',
        padding: '32px',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.08), 0 8px 20px rgba(0, 0, 0, 0.04)',
        textAlign: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, sans-serif'
      }}>
        
        {/* Success Icon */}
        <div style={{
          width: '72px',
          height: '72px',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px auto',
          boxShadow: '0 8px 24px rgba(16, 185, 129, 0.25)'
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <path 
              d="M9 12l2 2 4-4" 
              stroke="white" 
              strokeWidth="3" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Header */}
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#0F172A',
          margin: '0 0 8px 0',
          letterSpacing: '-0.02em'
        }}>
          Deployment Successful
        </h1>
        
        <p style={{
          fontSize: '16px',
          color: '#64748B',
          margin: '0 0 32px 0',
          lineHeight: '1.5'
        }}>
          Your website is now live and accessible worldwide
        </p>

        {/* Live Website Section */}
        <div style={{
          background: '#F1F5F9',
          border: '1px solid #CBD5E1',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{
            fontSize: '14px',
            color: '#64748B',
            marginBottom: '8px',
            fontWeight: '500'
          }}>
            Live Website URL
          </div>
          <div style={{
            fontFamily: 'Monaco, "SF Mono", Consolas, monospace',
            fontSize: '15px',
            color: '#0F172A',
            fontWeight: '600',
            marginBottom: '16px',
            wordBreak: 'break-all'
          }}>
            {url}
          </div>
          <button
            onClick={handleLaunchWebsite}
            style={{
              background: '#2563EB',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#1d4ed8';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#2563EB';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Launch Website →
          </button>
        </div>

        {/* Deployment Summary */}
        <div style={{
          background: '#FAFAFA',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '32px',
          textAlign: 'left'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#0F172A',
            margin: '0 0 16px 0'
          }}>
            Deployment Summary
          </h3>
          
          {/* Summary Items */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', color: '#64748B', fontWeight: '500' }}>
                Subdomain
              </span>
              <span style={{ fontSize: '14px', color: '#0F172A', fontWeight: '600' }}>
                {subdomain}
              </span>
            </div>
            
            <div style={{ height: '1px', background: '#E2E8F0', margin: '4px 0' }}></div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', color: '#64748B', fontWeight: '500' }}>
                Status
              </span>
              <span style={{
                fontSize: '12px',
                color: '#059669',
                background: '#D1FAE5',
                padding: '4px 8px',
                borderRadius: '6px',
                fontWeight: '600'
              }}>
                ACTIVE
              </span>
            </div>
            
            <div style={{ height: '1px', background: '#E2E8F0', margin: '4px 0' }}></div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', color: '#64748B', fontWeight: '500' }}>
                SSL Certificate
              </span>
              <span style={{
                fontSize: '12px',
                color: '#059669',
                background: '#D1FAE5',
                padding: '4px 8px',
                borderRadius: '6px',
                fontWeight: '600'
              }}>
                SECURED
              </span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: '14px',
          color: '#64748B',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ color: '#10b981', fontSize: '16px' }}>✓</span>
            <span>Share URL</span>
          </div>
          <div style={{ color: '#E2E8F0' }}>•</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ color: '#10b981', fontSize: '16px' }}>✓</span>
            <span>Update Content</span>
          </div>
          <div style={{ color: '#E2E8F0' }}>•</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ color: '#10b981', fontSize: '16px' }}>✓</span>
            <span>View Analytics</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button
            onClick={onCreateAnother}
            style={{
              background: '#F1F5F9',
              color: '#475569',
              border: '1px solid #CBD5E1',
              borderRadius: '8px',
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#E2E8F0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#F1F5F9';
            }}
          >
            Deploy Another
          </button>
          
          <button
            onClick={onClose}
            style={{
              background: '#0F172A',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#1E293B';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#0F172A';
            }}
          >
            Back to Dashboard
          </button>
        </div>

        {/* Footer */}
        <p style={{
          fontSize: '12px',
          color: '#64748B',
          margin: '24px 0 0 0',
          textAlign: 'center'
        }}>
          © 2025 Instant Deployer — Professional deployment solutions
        </p>

      </div>
    </div>
  );
};

export default DeploymentSuccess;